/** UI date display: MMM d, yyyy (unpadded day); datetimes: MMM d, yyyy HH:MM (24-hour, local). */

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function parseYmdPrefix(s: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mon = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(y) || mon < 1 || mon > 12 || day < 1 || day > 31) {
    return null;
  }
  return { y, m: mon, d: day };
}

/** Parse UI date `MMM d, yyyy` (accepts legacy padded day and normalizes). */
function parseDisplayDate(s: string): { y: number; m: number; d: number } | null {
  const m =
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{1,2}), (\d{4})$/.exec(s.trim());
  if (!m) return null;
  const monthIdx = MONTHS_SHORT.indexOf(m[1] as (typeof MONTHS_SHORT)[number]);
  if (monthIdx < 0) return null;
  const d = Number(m[2]);
  const y = Number(m[3]);
  if (!Number.isFinite(y) || d < 1 || d > 31) return null;
  return { y, m: monthIdx + 1, d };
}

/** Legacy numeric UI date accepted as input for backwards compatibility. */
function parseMdy(s: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s.trim());
  if (!m) return null;
  const mon = Number(m[1]);
  const day = Number(m[2]);
  const y = Number(m[3]);
  if (!Number.isFinite(y) || mon < 1 || mon > 12 || day < 1 || day > 31) {
    return null;
  }
  return { y, m: mon, d: day };
}

function formatShortMonthDate(y: number, m: number, d: number): string {
  return `${MONTHS_SHORT[m - 1]} ${d}, ${y}`;
}

export function formatDisplayDate(isoOrDate: string | Date | undefined | null): string {
  if (isoOrDate == null || isoOrDate === "") return "—";
  if (typeof isoOrDate === "string") {
    const display = parseDisplayDate(isoOrDate);
    if (display) {
      return formatShortMonthDate(display.y, display.m, display.d);
    }
    const legacyMdy = parseMdy(isoOrDate);
    if (legacyMdy) {
      return formatShortMonthDate(legacyMdy.y, legacyMdy.m, legacyMdy.d);
    }
    const ymd = parseYmdPrefix(isoOrDate);
    if (ymd) {
      return formatShortMonthDate(ymd.y, ymd.m, ymd.d);
    }
  }
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(d.getTime())) return "—";
  return formatShortMonthDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

export function formatDisplayDateTime(isoOrDate: string | Date | undefined | null): string {
  if (isoOrDate == null || isoOrDate === "") return "—";
  let d: Date;
  if (typeof isoOrDate === "string") {
    const trimmed = isoOrDate.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const ymd = parseYmdPrefix(trimmed);
      if (!ymd) return "—";
      const ld = new Date(ymd.y, ymd.m - 1, ymd.d);
      if (Number.isNaN(ld.getTime())) return "—";
      d = ld;
    } else {
      d = new Date(trimmed);
    }
  } else {
    d = isoOrDate;
  }
  if (Number.isNaN(d.getTime())) return "—";
  return `${formatDisplayDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
